/* eslint-disable react/prop-types */
import {useEffect, useRef, useState} from 'react'
import {
    IconCapsule,
    IconCircleLayout,
    IconDogBone, IconFill, IconHeart,
    IconMatricula,
    IconComicBubble,
    IconHousePlate,
    IconClapperboard,
    IconOffset,
    IconSquare,
} from "../ToolSelectorTop/Icons.jsx";
import {IconPlantTag} from "../ToolSelectorTop/IconPlantTag.jsx";
import ColorPicker from "./components/ColorPicker.jsx";

// import "./ToolSelectoRight.css"
import { useTools } from "../../ToolProvider.jsx";
import {useEditorTypeRightContext, useEditorTypeRightToggleContext} from "../../SelectedTypeProvider.jsx";
import {
    useDataModelo3dContext,
    useDataModelo3dToggleContext,
    useOffsetLengthContext,
    useOffsetLengthToggleContext
} from "../../DataProvider.jsx";
import ThiknessAndMoreEditor from "./components/ThiknessAndMoreEditor.jsx";
import ParamPanel from "./components/ParamPanel/ParamPanel.jsx";
import ManifoldDiagnostics from "./components/ParamPanel/ManifoldDiagnostics.jsx";
import {DEFAULT_PRODUCT, getProduct, patchPath} from "../../../../products/registry";
import TextEditor from "./components/TextEditor/TextEditor.jsx";
import HistoryPanel from "./components/HistoryPanel/HistoryPanel.jsx";
import LayerPanel from "./components/LayerPanel/LayerPanel.jsx";
import PartVisibilityPanel from "./components/PartVisibilityPanel/PartVisibilityPanel.jsx";
import ShapePanel from "./components/ShapePanel/ShapePanel.jsx";
import OffsetDrawingPanel from "./components/OffsetDrawingPanel/OffsetDrawingPanel.jsx";
import InternalElementsPanel from "./components/InternalElementsPanel/InternalElementsPanel.jsx";
import NapkinJointPanel from "./components/NapkinJointPanel/NapkinJointPanel.jsx";
import InputSpinner2 from "./components/InputSpinner2/InputSpinner2.jsx";
import useVisualViewport from "../../../../components/hooks/useVisualViewport.js";
import {DEFAULT_PLANT_TAG_PARAMS, normalizePlantTagParams} from "../../SVGBaseLayouts/SVGBaseLayouts.jsx";

const PX_TO_MM = 0.2645833333

// Tope de ancho y alto de la base, en milímetros. Muy por encima de una cama de
// impresión a propósito: un diseño grande se parte en piezas o se imprime a
// escala, y el editor no es quien debe decidir eso. Es el único límite de la
// cadena — el spinner no acota por defecto y el backend tampoco.
const BASE_SIZE_MAX_MM = 5000

const ToolSelectorRight = ({
    onChangeBaseLayout,
    baseLayoutActionState = {},
    manifoldPoc = {},
    parts3d = {},
    product = getProduct(DEFAULT_PRODUCT),
    resizeDrawingsWithBase = false,
    onResizeDrawingsWithBaseChange,
    onBaseSizeSliderChange,
}) => {
    const [includeHoleInBaseLayout, setIncludeHoleInBaseLayout] = useState(false)
    const [highPrecisionOffsetFill, setHighPrecisionOffsetFill] = useState(false)
    // Cuanto se mete el fondo por debajo del dibujo, en mm. Encoger el fondo
    // es lo que evita que asome; agrandar los dibujos antes de calcular haria
    // el fondo mas grande, al reves de lo que se busca.
    const [backgroundInset, setBackgroundInset] = useState(0)
    const [offsetJoinType, setOffsetJoinType] = useState('round')
    const [offsetDrawingsFor3d, setOffsetDrawingsFor3d] = useState(false)

    const handleAtrasButton = () => {
        setEditorTypeRight({ ... editorTypeRight, type: null})
    }

    const dataModelo3d = useDataModelo3dContext()
    const setDataModelo3d = useDataModelo3dToggleContext()

    useEffect(() => {
        setOffsetDrawingsFor3d(Boolean(dataModelo3d?.baseLayout?.offsetDrawings))
    }, [dataModelo3d?.baseLayout?.offsetDrawings])


    const offsetLength = useOffsetLengthContext()
    const setOffsetLength = useOffsetLengthToggleContext()

    const {
        onHoleActivation: holeActivated,
        onAddKeyhole, onRemoveKeyhole, onResizeKeyhole, getKeyholes,
        onAddScrew, onRemoveScrew, onResizeScrew, getScrews,
        hasHoles, canvas,
    } = useTools()

    // Qué habilita "Incluir agujero": que haya algún agujero en el lienzo, sea
    // el hueco de colgar, uno de la operación "Agujero pasante" o una ranura.
    // `dataModelo3d.holeExists` sólo conoce el primero, y con los otros dos el
    // checkbox se quedaba gris para siempre.
    const [canIncludeHole, setCanIncludeHole] = useState(false)

    useEffect(() => {
        if (!canvas) return undefined
        const refresh = () => setCanIncludeHole(Boolean(hasHoles?.()))
        refresh()
        const events = ['object:added', 'object:removed', 'object:modified', 'layer:changed']
        events.forEach(event => canvas.on(event, refresh))
        return () => events.forEach(event => canvas.off(event, refresh))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas])

    useEffect(() => {
        if (!canIncludeHole) setIncludeHoleInBaseLayout(false)
    }, [canIncludeHole])


    // contesto para controlar el editor de la derecha
    const editorTypeRight = useEditorTypeRightContext()
    const setEditorTypeRight = useEditorTypeRightToggleContext()
    const { viewportHeight, keyboardOpen, keyboardHeight } = useVisualViewport()
    const isTextEditor = editorTypeRight.type === 'editor_text'
    const keyboardPanelStyle = isTextEditor && keyboardOpen
        ? { maxHeight: `${Math.max(viewportHeight - 24, 260)}px` }
        : undefined
    const keyboardContentStyle = isTextEditor && keyboardOpen
        ? { paddingBottom: `${Math.min(keyboardHeight, 240) + 16}px` }
        : undefined
    const isBaseLayoutLoading = Boolean(baseLayoutActionState.loading)
    const activeBaseLayoutAction = baseLayoutActionState.action
    const baseLayoutError = baseLayoutActionState.error
    // The full catalogue; each product picks from it via `product.layouts`, so a
    // lightbox is not offered the concave shapes whose interior cannot be
    // hollowed out reliably.
    const BASE_LAYOUT_CATALOG = [
        { key: 'square', label: 'Cuadrado', title: 'Fondo cuadrado', Icon: IconSquare },
        { key: 'circle', label: 'Círculo', title: 'Fondo redondo', Icon: IconCircleLayout },
        { key: 'dog', label: 'Hueso', title: 'Fondo hueso', Icon: IconDogBone },
        { key: 'heart', label: 'Corazón', title: 'Fondo corazón', Icon: IconHeart },
        { key: 'matricula', label: 'Matrícula', title: 'Fondo matrícula', Icon: IconMatricula },
        { key: 'capsule', label: 'Cápsula', title: 'Fondo cápsula', Icon: IconCapsule },
        { key: 'plant_tag', label: 'Etiqueta plantas', title: 'Etiqueta para plantas con estaca', Icon: IconPlantTag },
        { key: 'comic', label: 'Bocadillo', title: 'Bocadillo de cómic', Icon: IconComicBubble },
        { key: 'casa', label: 'Casa', title: 'Placa de casa con número', Icon: IconHousePlate },
        { key: 'claqueta', label: 'Claqueta', title: 'Claqueta de cine', Icon: IconClapperboard },
    ]
    // Titles default to these and a product may override any of them, so
    // "Agujero de llavero" does not follow a lightbox around.
    const DEFAULT_PANEL_TITLES = {
        editor_hole: 'Elementos técnicos',
        editor_base_layout: 'Base/Cuerpo',
        editor_color: 'Edición de color',
        editor_params_3d: 'Parámetros 3D',
        editor_text: 'Edición de texto',
        editor_history: 'Historial',
        editor_layers: 'Gestión de capas',
        editor_pieces_3d: 'Piezas del modelo',
        editor_shape: 'Esquinas redondeadas',
        editor_offset_drawing: 'Contorno como dibujo',
    }
    const panelTitle = product?.titles?.[editorTypeRight.type]
        ?? DEFAULT_PANEL_TITLES[editorTypeRight.type]
        ?? ''

    const allowedLayouts = product?.layouts
    const baseLayoutShapes = allowedLayouts
        ? BASE_LAYOUT_CATALOG.filter((shape) => allowedLayouts.includes(shape.key))
        : BASE_LAYOUT_CATALOG
    const otherBaseLayoutActions = [
        {
            key: 'offset',
            label: 'Offset',
            description: 'Expandir contorno',
            title: 'Aplicar offset',
            Icon: IconOffset,
        },
        {
            key: 'fill',
            label: 'Fill',
            description: 'Rellenar huecos',
            title: 'Aplicar fill',
            Icon: IconFill,
        },
    ]
    const offsetJoinTypeOptions = [
        { value: 'round', label: 'Redondeado' },
        { value: 'miter', label: 'Punta' },
        { value: 'square', label: 'Recto' },
    ]

    const toggleHoleVisibility = () => {
        const currentHoleExists = dataModelo3d.holeExists;
        setDataModelo3d({ holeExists: !currentHoleExists });
        // setDataModelo3d2(!dataModelo3d.holeExists);
        holeActivated(!dataModelo3d.holeExists);
    };

    // Los marcadores viven en el lienzo (se guardan con el diseño), así que la
    // lista se relee del editor en vez de duplicarse en el estado de React.
    const [keyholes, setKeyholes] = useState([])
    const refreshKeyholes = () => setKeyholes(getKeyholes?.() ?? [])

    useEffect(() => {
        if (editorTypeRight.type === 'editor_hole') setKeyholes(getKeyholes?.() ?? [])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorTypeRight.type])

    // El SVG se carga de forma asíncrona en Fabric, así que la lista se relee
    // en el siguiente frame en lugar de asumir el resultado.
    const handleAddKeyhole = () => {
        onAddKeyhole?.()
        setTimeout(refreshKeyholes, 60)
    }

    const handleRemoveKeyhole = (keyholeId) => {
        onRemoveKeyhole?.(keyholeId)
        setTimeout(refreshKeyholes, 60)
    }

    const handleResizeKeyhole = (keyholeId, size) => {
        onResizeKeyhole?.(keyholeId, size)
        setTimeout(refreshKeyholes, 60)
    }

    // Avellanados pasantes: misma mecánica que las ranuras (la lista vive en el
    // lienzo y se relee un frame después, porque Fabric parsea el SVG async).
    const [screws, setScrews] = useState([])
    const refreshScrews = () => setScrews(getScrews?.() ?? [])

    useEffect(() => {
        if (editorTypeRight.type === 'editor_hole') setScrews(getScrews?.() ?? [])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorTypeRight.type])

    const handleAddScrew = () => {
        onAddScrew?.()
        setTimeout(refreshScrews, 60)
    }

    const handleRemoveScrew = (screwId) => {
        onRemoveScrew?.(screwId)
        setTimeout(refreshScrews, 60)
    }

    const handleResizeScrew = (screwId, size) => {
        onResizeScrew?.(screwId, size)
        setTimeout(refreshScrews, 60)
    }


    useEffect(() => {
        console.log('editorTypeRightasdf', editorTypeRight)

    }, [editorTypeRight]);



    const [proportionLocked, setProportionLocked] = useState(false)
    const lockedRatio = useRef(null) // height / width
    const [holeSize, setHoleSize] = useState(null)

    useEffect(() => {
        if (!canvas || !dataModelo3d.holeExists) {
            setHoleSize(null)
            return undefined
        }

        const updateHoleSize = () => {
            const hole = canvas.getObjects().find(obj => String(obj?.id ?? '').startsWith('hole'))
            if (!hole) {
                setHoleSize(null)
                return
            }

            setHoleSize({
                width: hole.getScaledWidth() * PX_TO_MM,
                height: hole.getScaledHeight() * PX_TO_MM,
            })
        }

        updateHoleSize()
        canvas.on('object:added', updateHoleSize)
        canvas.on('object:removed', updateHoleSize)
        canvas.on('object:scaling', updateHoleSize)
        canvas.on('object:modified', updateHoleSize)

        return () => {
            canvas.off('object:added', updateHoleSize)
            canvas.off('object:removed', updateHoleSize)
            canvas.off('object:scaling', updateHoleSize)
            canvas.off('object:modified', updateHoleSize)
        }
    }, [canvas, dataModelo3d.holeExists])

    const toggleProportionLock = () => {
        if (!proportionLocked) {
            const w = dataModelo3d.baseLayout.width
            const h = dataModelo3d.baseLayout.height
            if (w && h && w !== 0) {
                lockedRatio.current = h / w
            }
        }
        setProportionLocked(prev => !prev)
    }

    const handleWidth = (newWidth) => {
        onBaseSizeSliderChange?.()
        const update = { width: newWidth }
        if (proportionLocked && lockedRatio.current !== null && newWidth !== 0) {
            update.height = Math.round(newWidth * lockedRatio.current * 10) / 10
        }
        setDataModelo3d({
            ...dataModelo3d,
            baseLayout: { ...dataModelo3d.baseLayout, ...update },
        });
    };
    const handleHeight = (newHeight) => {
        onBaseSizeSliderChange?.()
        const update = { height: newHeight }
        if (proportionLocked && lockedRatio.current !== null && newHeight !== 0) {
            update.width = Math.round((newHeight / lockedRatio.current) * 10) / 10
        }
        setDataModelo3d({
            ...dataModelo3d,
            baseLayout: { ...dataModelo3d.baseLayout, ...update },
        });
    };
    const handleOffsetLength = (newOffsetLength) => {

        setOffsetLength(newOffsetLength);
        // setDataModelo3d({
        //     ...dataModelo3d,
        //     baseLayout: {
        //         ...dataModelo3d.baseLayout,
        //         height: newOffsetLength,
        //     },
        // });
    };

    const isPlantTag = dataModelo3d.baseLayout?.key === 'plant_tag'
    const plantTagParams = normalizePlantTagParams(
        isPlantTag ? dataModelo3d.baseLayout?.params : DEFAULT_PLANT_TAG_PARAMS,
    )
    const handlePlantTagParam = (key, value) => {
        onChangeBaseLayout('plant_tag', {
            layoutParams: { ...plantTagParams, [key]: value },
        })
    }

    // Ranuras y tornillos de colgado. En la caja de luz son el panel entero;
    // en el llavero van dentro de los elementos internos, como un tipo más.
    const hangingSections = (
        <>
            {product?.roles?.keyhole && (
                <div className="editor-container space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm">
                            {keyholes.length
                                ? `${keyholes.length} ${keyholes.length === 1 ? 'ranura' : 'ranuras'} en el diseño`
                                : 'Sin ranuras de colgado'}
                        </span>
                        <button
                            type="button"
                            className="btn btn-xs btn-primary"
                            onClick={handleAddKeyhole}
                        >
                            Añadir ranura
                        </button>
                    </div>

                    {keyholes.length > 0 && (
                        <ul className="flex flex-col gap-3">
                            {keyholes.map((keyhole) => (
                                <li
                                    // Keyed by the marker's number, not its id: the id encodes
                                    // the size, so keying by it would remount the whole row on
                                    // every edit and throw away the focus mid-typing.
                                    key={keyhole.index}
                                    className="rounded-lg border border-base-300 bg-base-200 p-3"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold">Ranura {keyhole.index}</span>
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-ghost text-error"
                                            onClick={() => handleRemoveKeyhole(keyhole.id)}
                                        >
                                            Quitar
                                        </button>
                                    </div>

                                    <div className="mt-2 grid grid-cols-3 gap-2">
                                        {[
                                            { key: 'headDiameterMm', label: 'Cabeza', min: 4, max: 20, step: 0.5 },
                                            { key: 'shankDiameterMm', label: 'Cuello', min: 2, max: 12, step: 0.2 },
                                            { key: 'slotLengthMm', label: 'Recorrido', min: 3, max: 30, step: 0.5 },
                                        ].map(({ key, label, min, max, step }) => {
                                            // Committed on blur/Enter rather than per keystroke:
                                            // each edit rebuilds the shape on the canvas and the
                                            // value only comes back a frame later, so a controlled
                                            // input would fight whatever is being typed.
                                            const commit = (event) => {
                                                const next = Number(event.target.value)
                                                if (!Number.isFinite(next) || next < min || next > max) {
                                                    event.target.value = keyhole[key]
                                                    return
                                                }
                                                if (next === keyhole[key]) return
                                                handleResizeKeyhole(keyhole.id, { [key]: next })
                                            }
                                            return (
                                                <label key={key} className="flex flex-col gap-1">
                                                    <span className="text-[11px] opacity-60">{label} (mm)</span>
                                                    <input
                                                        // Re-keyed on the id so the field picks up
                                                        // whatever the editor clamped the value to,
                                                        // but only once it has already lost focus.
                                                        key={keyhole.id}
                                                        type="number"
                                                        className="input input-xs input-bordered w-full"
                                                        defaultValue={keyhole[key]}
                                                        min={min}
                                                        max={max}
                                                        step={step}
                                                        onBlur={commit}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') event.target.blur()
                                                        }}
                                                    />
                                                </label>
                                            )
                                        })}
                                    </div>

                                    <label className="mt-2 flex items-center gap-2">
                                        <span className="text-[11px] opacity-60">Ranura hacia</span>
                                        <select
                                            className="select select-xs select-bordered flex-1"
                                            value={keyhole.direction}
                                            onChange={(event) =>
                                                handleResizeKeyhole(keyhole.id, { direction: event.target.value })
                                            }
                                        >
                                            <option value="u">Arriba (cuelga)</option>
                                            <option value="d">Abajo</option>
                                            <option value="l">Izquierda</option>
                                            <option value="r">Derecha</option>
                                        </select>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-snug">
                        Arrastra cada marcador por el lienzo para elegir dónde va su ranura, y
                        ajusta su tamaño con estos campos. <strong>No se redimensiona
                        arrastrando</strong>: al estirar, el círculo se deformaría en óvalo en vez
                        de cambiar de diámetro. Cada ranura se configura por separado; en piezas
                        anchas, dos cuelgan más recto que una. Sin ninguna ranura añadida no se
                        corta nada.
                        <br /><br />
                        La ranura va <strong>hacia arriba</strong>: el tornillo entra por el
                        círculo, la pieza baja y queda apoyada en el extremo alto de la ranura.
                        Con la ranura hacia abajo, al bajar la pieza el tornillo vuelve al
                        círculo grande y se suelta.
                    </div>
                </div>
            )}
            {product?.roles?.screw && (
                <div className="editor-container mt-6 space-y-3 border-t border-base-300 pt-4">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm">
                            {screws.length
                                ? `${screws.length} ${screws.length === 1 ? 'tornillo' : 'tornillos'} en el diseño`
                                : 'Sin tornillos pasantes'}
                        </span>
                        <button
                            type="button"
                            className="btn btn-xs btn-primary"
                            onClick={handleAddScrew}
                        >
                            Añadir tornillo
                        </button>
                    </div>

                    {screws.length > 0 && (
                        <ul className="flex flex-col gap-3">
                            {screws.map((screw) => (
                                // Igual que las ranuras: la clave es el número del
                                // marcador, no su id (que lleva el tamaño dentro y
                                // remontaría la fila en mitad de la edición).
                                <li
                                    key={screw.index}
                                    className="rounded-lg border border-base-300 bg-base-200 p-3"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold">Tornillo {screw.index}</span>
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-ghost text-error"
                                            onClick={() => handleRemoveScrew(screw.id)}
                                        >
                                            Quitar
                                        </button>
                                    </div>

                                    <div className="mt-2 grid grid-cols-2 gap-2">
                                        {[
                                            { key: 'headDiameterMm', label: 'Cabeza', min: 3, max: 20, step: 0.5 },
                                            { key: 'shankDiameterMm', label: 'Rosca', min: 1.5, max: 12, step: 0.2 },
                                        ].map(({ key, label, min, max, step }) => {
                                            const commit = (event) => {
                                                const next = Number(event.target.value)
                                                if (!Number.isFinite(next) || next < min || next > max) {
                                                    event.target.value = screw[key]
                                                    return
                                                }
                                                if (next === screw[key]) return
                                                handleResizeScrew(screw.id, { [key]: next })
                                            }
                                            return (
                                                <label key={key} className="flex flex-col gap-1">
                                                    <span className="text-[11px] opacity-60">{label} (mm)</span>
                                                    <input
                                                        key={screw.id}
                                                        type="number"
                                                        className="input input-xs input-bordered w-full"
                                                        defaultValue={screw[key]}
                                                        min={min}
                                                        max={max}
                                                        step={step}
                                                        onBlur={commit}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') event.target.blur()
                                                        }}
                                                    />
                                                </label>
                                            )
                                        })}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {product?.key === 'keyring' ? (
                        <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-snug">
                            Sirve para colgar cualquier pieza de este editor: un llavero, un
                            posavasos o un rótulo. El tornillo pasante es la <strong>alternativa a la
                            ranura</strong>: en vez de colgar la pieza de un tornillo ya puesto, se
                            atornilla a la pared. El agujero atraviesa la placa y su avellanado se abre
                            <strong> por delante</strong>, para que la cabeza cónica quede enrasada con
                            el dibujo y la cara trasera siga plana contra la pared. El marcador tiene
                            que quedar entero dentro de la pieza, a 1,5 mm del borde como mínimo. El
                            cono es de 90° (métrico). Con corcho, la ranura tiene que quedar fuera de
                            él: el corcho tapa la cara de atrás.
                        </div>
                    ) : (
                        <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-snug">
                            El tornillo pasante es la <strong>alternativa a la ranura</strong>: en vez de
                            colgar la caja de un tornillo ya puesto, se atornilla la caja a la pared y
                            después se encaja el panel frontal. El agujero atraviesa la base y su
                            avellanado se abre <strong>hacia dentro</strong>, para que la cabeza cónica
                            quede enrasada y la cara trasera siga plana contra la pared. Por eso el
                            marcador tiene que caer dentro del hueco, no sobre la pared. El ángulo del
                            cono se ajusta en los parámetros de la caja (90° métrico por defecto).
                        </div>
                    )}
                </div>
            )}
        </>
    )

    return (
        <div className="flex flex-col max-h-full w-full h-full " style={keyboardPanelStyle}>

            <div className=" flex justify-between p-3 border-b">

                <h2 className='text-xl  justify-start flex items-center'>
                    {panelTitle}
                </h2>
                <button type="submit"
                        className="btn btn-sm btn-primary text-accent-content justify-end "
                        onClick={handleAtrasButton}
                >
                    Atras
                </button>

            </div>
            <div className="overflow-y-scroll z-50 w-full px-3 h-full pt-4" style={keyboardContentStyle}>
                <div className=" w-full h-full">
                    {
                        editorTypeRight.type === 'editor_hole' && product?.roles?.napkinJoint &&
                        <div className="mb-6 border-b border-base-300 pb-4">
                            <NapkinJointPanel defaultHeightMm={Number(dataModelo3d?.panel?.tongueHeightMm) || 8}/>
                        </div>
                    }
                    {
                        editorTypeRight.type === 'editor_hole' && product?.roles?.internalElements &&
                        <InternalElementsPanel
                            hangingPanel={product?.roles?.keyhole || product?.roles?.screw ? hangingSections : null}
                            showCrocs={Boolean(product?.roles?.crocs)}
                        />
                    }
                    {
                        editorTypeRight.type === 'editor_hole' && !product?.roles?.internalElements && hangingSections
                    }
                    {
                        editorTypeRight.type === 'editor_hole' && !product?.roles?.keyhole && !product?.roles?.screw && !product?.roles?.internalElements &&

                        <div className="editor-container">

                            {/*<div className="editor">*/}
                            {/*    /!* Render the hole conditionally based on showHole state *!/*/}
                            {/*    {dataModelo3d.holeExists && <div className="hole"></div>}*/}
                            {/*</div>*/}
                            {/* Checkbox to toggle hole visibility */}

                            <label className="label cursor-pointer" >

                                <span className="label-text mr-2">Quitar o poner agujero</span>

                                <input
                                    checked={dataModelo3d.holeExists}
                                    onChange={toggleHoleVisibility}

                                    type="checkbox"
                                    className="checkbox checboxMod" />
                            </label>

                            {dataModelo3d.holeExists && (
                                <div className="mt-3 border-t pt-3 space-y-3">
                                    <div className="rounded-lg border border-base-300 bg-base-200 p-3">
                                        <p className="text-sm font-semibold">Tamaño actual del agujero</p>
                                        {holeSize ? (
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                <div className="rounded-md bg-base-100 p-2 text-center">
                                                    <span className="block text-xs opacity-60">Ancho</span>
                                                    <strong>{holeSize.width.toFixed(2)} mm</strong>
                                                </div>
                                                <div className="rounded-md bg-base-100 p-2 text-center">
                                                    <span className="block text-xs opacity-60">Alto</span>
                                                    <strong>{holeSize.height.toFixed(2)} mm</strong>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="mt-2 text-sm opacity-60">Calculando tamaño...</p>
                                        )}
                                        <p className="mt-2 text-xs opacity-60">
                                            Si ancho y alto coinciden, esa medida corresponde al diámetro.
                                        </p>
                                    </div>

                                    <div role="alert" className="rounded-lg border border-info/40 bg-info/10 p-3 text-sm">
                                        <p className="font-semibold">Diámetro recomendado</p>
                                        <p className="mt-1">
                                            Usa aproximadamente <strong>2,2 mm</strong> para una leontina pequeña.
                                            Si vas a colocar una anilla, deja al menos <strong>5 mm</strong>.
                                        </p>
                                        <p className="mt-2 text-xs opacity-70">
                                            Recomendación orientativa: comprueba el grosor real del accesorio y deja holgura suficiente.
                                        </p>
                                    </div>

                                    <div>
                                        <span className="label-text text-sm opacity-70">¿Qué perfora el agujero?</span>
                                        <div className="flex flex-col gap-1 mt-1">
                                            <label className="label cursor-pointer justify-start gap-2">
                                                <input
                                                    type="radio"
                                                    name="holePerforatesMode"
                                                    className="radio radio-sm"
                                                    checked={!dataModelo3d.holePerforatesDrawings}
                                                    onChange={() => setDataModelo3d({ holePerforatesDrawings: false })}
                                                />
                                                <span className="label-text">Solo el fondo</span>
                                            </label>
                                            <label className="label cursor-pointer justify-start gap-2">
                                                <input
                                                    type="radio"
                                                    name="holePerforatesMode"
                                                    className="radio radio-sm"
                                                    checked={dataModelo3d.holePerforatesDrawings}
                                                    onChange={() => setDataModelo3d({ holePerforatesDrawings: true })}
                                                />
                                                <span className="label-text">El fondo y los dibujos</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    }
                    {
                        editorTypeRight.type === 'editor_base_layout' &&

                        <>
                            <>
                                <h3 className="mb-2 text-base font-semibold">
                                    {isPlantTag ? 'Medidas de la etiqueta' : 'Dimensiones'}
                                </h3>
                                {isPlantTag ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { key: 'plateWidth', label: 'Ancho placa', min: 30, max: 200 },
                                            { key: 'plateHeight', label: 'Alto placa', min: 12, max: 80 },
                                            { key: 'stakeWidth', label: 'Ancho pincho', min: 2, max: 20 },
                                            { key: 'stakeHeight', label: 'Alto pincho', min: 25, max: 250 },
                                        ].map(({ key, label, min, max }) => (
                                            <div key={key} className="flex min-w-0 flex-col gap-1">
                                                <label className="text-sm" htmlFor={`plant-tag-${key}`}>{label} (mm)</label>
                                                <InputSpinner2
                                                    id={`plant-tag-${key}`}
                                                    value={plantTagParams[key]}
                                                    onChange={(value) => handlePlantTagParam(key, value)}
                                                    min={min}
                                                    max={max}
                                                    initialStep={1}
                                                />
                                            </div>
                                        ))}
                                        <p className="col-span-2 text-xs text-base-content/60">
                                            Tamaño total: {plantTagParams.plateWidth} × {plantTagParams.plateHeight + plantTagParams.stakeHeight} mm
                                        </p>
                                    </div>
                                ) : (
                                <div className="flex flex-col lg:flex-row w-full gap-2 items-end">
                                    <div className="flex-1 flex flex-col">
                                        <label htmlFor="width">Ancho:</label>
                                        <InputSpinner2
                                            value={dataModelo3d.baseLayout.width}
                                            onChange={handleWidth}
                                            min={0}
                                            max={BASE_SIZE_MAX_MM}
                                            initialStep={1}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={toggleProportionLock}
                                        title={proportionLocked ? 'Proporción bloqueada (click para desbloquear)' : 'Proporción libre (click para bloquear)'}
                                        className={`btn btn-sm mb-0.5 px-2 ${proportionLocked ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                                    >
                                        {proportionLocked ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                                <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75a3.75 3.75 0 10-7.5 0v3h.75a3 3 0 013 3v6.75a3 3 0 01-3 3H3.75a3 3 0 01-3-3v-6.75a3 3 0 013-3H15v-3C15 3.85 16.35 1.5 18 1.5z" />
                                            </svg>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onResizeDrawingsWithBaseChange?.(!resizeDrawingsWithBase)}
                                        title={resizeDrawingsWithBase
                                            ? 'Redimensionar fondo y dibujos juntos'
                                            : 'Redimensionar solo el fondo'}
                                        className={`btn btn-sm mb-0.5 px-2 ${resizeDrawingsWithBase ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                            <rect x="3.5" y="5" width="10" height="10" rx="1.5" />
                                            <path d="M14.5 9h3.75A1.75 1.75 0 0 1 20 10.75v6.75a1.75 1.75 0 0 1-1.75 1.75H9.5A1.75 1.75 0 0 1 7.75 17.5V16" />
                                            <path d="M5 3.5h4M3.5 5v4M19 15v4M15 20.5h4" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                    <div className="flex-1 flex flex-col">
                                        <label htmlFor="height">Alto:</label>
                                        <InputSpinner2
                                            value={dataModelo3d.baseLayout.height}
                                            onChange={handleHeight}
                                            min={0}
                                            max={BASE_SIZE_MAX_MM}
                                            initialStep={1}
                                        />
                                    </div>
                                </div>
                                )}
                                <div className="divider mt-2 "></div>
                            </>
                            <div className="collapse collapse-arrow bg-base-200">
                                <input type="checkbox" name="accordion-1" id="accordion-1" />
                                <label htmlFor="accordion-1" className="collapse-title text-xl font-medium cursor-pointer">
                                    Formas
                                </label>
                                <div className="collapse-content gap-2 grid grid-cols-2 ">
                                    {baseLayoutShapes.map(({ key, label, title, Icon }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => onChangeBaseLayout(key)}
                                            title={title}
                                            className="card min-h-[96px] flex flex-col items-center justify-center p-3 bg-gray-100 border border-gray-300 rounded-lg transition-colors hover:bg-gray-200 cursor-pointer text-black"
                                        >
                                            <Icon className="w-10 h-10 mt-1 z-10" />
                                            <span className="mt-2 text-sm font-semibold leading-tight">{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="divider mt-2"></div>

                            <div className="collapse collapse-arrow bg-base-200">
                                <input type="checkbox" name="accordion-2" id="accordion-2" />
                                <label htmlFor="accordion-2" className="collapse-title text-xl font-medium cursor-pointer">
                                    Otras formas
                                </label>
                                <div className="collapse-content gap-2 grid grid-cols-2 ">

                                    {otherBaseLayoutActions.map(({ key, label, description, title, Icon }) => {
                                        const isLoading = isBaseLayoutLoading && activeBaseLayoutAction === key

                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => onChangeBaseLayout(key, {
                                                    includeHole: includeHoleInBaseLayout,
                                                    highPrecision: highPrecisionOffsetFill,
                                                    offsetJoinType,
                                                    backgroundInset: highPrecisionOffsetFill ? backgroundInset : 0,
                                                    offsetDrawings: offsetDrawingsFor3d,
                                                })}
                                                disabled={isBaseLayoutLoading}
                                                title={title}
                                                aria-label={title}
                                                className={`group relative min-h-[112px] rounded-lg border border-base-300 bg-base-100 p-3 text-base-content shadow-sm transition-all ${
                                                    isBaseLayoutLoading
                                                        ? 'opacity-70 cursor-wait'
                                                        : 'cursor-pointer hover:border-primary/60 hover:bg-base-200 hover:shadow-md'
                                                }`}
                                            >
                                                {highPrecisionOffsetFill && (
                                                    <span
                                                        className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-black text-primary-content ring-2 ring-primary/25"
                                                        aria-label="Maximo detalle activo"
                                                        title="Maximo detalle activo"
                                                    >
                                                        ✓
                                                    </span>
                                                )}
                                                {isLoading ? (
                                                    <span className="loading loading-spinner loading-md mb-2"></span>
                                                ) : (
                                                    <Icon className="mb-2 h-10 w-10 text-base-content/90 transition-colors group-hover:text-primary" />
                                                )}
                                                <span className="text-sm font-semibold leading-tight">
                                                    {isLoading ? 'Calculando...' : label}
                                                </span>
                                                <span className="mt-1 text-xs leading-tight text-base-content/60">{description}</span>
                                            </button>
                                        )
                                    })}

                                    {baseLayoutError && (
                                        <div className="col-span-2 rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                                            {baseLayoutError}
                                        </div>
                                    )}


                                    <div className="col-span-2">
                                        <label className="mb-1 block text-sm font-medium" htmlFor="offset_length">Distancia offset:</label>

                                        <InputSpinner2
                                            value={offsetLength}
                                            onChange={handleOffsetLength}
                                            min={0}
                                            max={1000} // Define un máximo si lo deseas
                                            initialStep={1}
                                            interval={100} // Ajusta la rapidez del cambio continuo
                                        />
                                    </div>

                                    <label className={`col-span-2 flex items-center justify-between gap-3 rounded-lg border border-base-300 p-3 ${canIncludeHole ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                        <div>
                                            <span className="block text-sm font-medium">Incluir agujero</span>
                                            <span className="block text-xs text-base-content/60">
                                                {canIncludeHole
                                                    ? 'Hace que los agujeros participen en el cálculo de Fill y Offset.'
                                                    : 'No hay ningún agujero en el diseño todavía.'}
                                            </span>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-primary"
                                            checked={includeHoleInBaseLayout && canIncludeHole}
                                            disabled={!canIncludeHole || isBaseLayoutLoading}
                                            onChange={(event) => setIncludeHoleInBaseLayout(event.target.checked)}
                                        />
                                    </label>

                                    <label className={`col-span-2 flex items-center justify-between gap-3 rounded-lg border border-base-300 p-3 ${isBaseLayoutLoading ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}>
                                        <div>
                                            <span className="block text-sm font-medium">Máximo detalle</span>
                                            <span className="block text-xs text-base-content/60">
                                                Usa más precisión en curvas y evita simplificar el SVG resultante.
                                            </span>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-primary"
                                            checked={highPrecisionOffsetFill}
                                            disabled={isBaseLayoutLoading}
                                            onChange={(event) => setHighPrecisionOffsetFill(event.target.checked)}
                                        />
                                    </label>

                                    {highPrecisionOffsetFill && (
                                        <div className="col-span-2">
                                            <label className="mb-1 block text-sm font-medium" htmlFor="background_inset">
                                                Recorte del fondo (mm):
                                            </label>
                                            <InputSpinner2
                                                value={backgroundInset}
                                                onChange={setBackgroundInset}
                                                min={0}
                                                max={10}
                                                initialStep={0.1}
                                            />
                                            <span className="mt-1 block text-xs text-base-content/60">
                                                Mete el fondo por debajo del dibujo para que no asome por los bordes.
                                            </span>
                                        </div>
                                    )}

                                    <label className="col-span-2 flex items-center justify-between gap-3 rounded-lg border border-base-300 p-3 cursor-pointer">
                                        <div>
                                            <span className="block text-sm font-medium">Aplicar offset a los drawings en 3D</span>
                                            <span className="block text-xs text-base-content/60">
                                                Usa la misma distancia del background para el cutter 3D. Mantiene la edición 2D intacta.
                                            </span>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-primary"
                                            checked={offsetDrawingsFor3d}
                                            disabled={isBaseLayoutLoading}
                                            onChange={(event) => {
                                                const enabled = event.target.checked
                                                const storedOffset = Number(dataModelo3d.baseLayout?.drawingsOffsetMm)
                                                setOffsetDrawingsFor3d(enabled)
                                                setDataModelo3d({
                                                    baseLayout: {
                                                        ...dataModelo3d.baseLayout,
                                                        offsetDrawings: enabled,
                                                        drawingsOffsetMm: enabled
                                                            ? (Number.isFinite(storedOffset) ? storedOffset : Number(offsetLength))
                                                            : 0,
                                                    },
                                                })
                                            }}
                                        />
                                    </label>

                                    <div className="col-span-2">
                                        <label className="mb-1 block text-sm font-medium" htmlFor="offset_join_type">
                                            Tipo de offset:
                                        </label>
                                        <select
                                            id="offset_join_type"
                                            className="select select-bordered select-sm w-full bg-base-100 text-base-content"
                                            value={offsetJoinType}
                                            disabled={isBaseLayoutLoading}
                                            onChange={(event) => setOffsetJoinType(event.target.value)}
                                        >
                                            {offsetJoinTypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>


                                </div>
                            </div>
                            <div className="divider mt-2"></div>
                            <div className="collapse collapse-arrow bg-base-200">
                                <input type="checkbox" name="accordion-3" id="accordion-3" />
                                <label htmlFor="accordion-3" className="collapse-title text-xl font-medium cursor-pointer">
                                    Colores
                                </label>
                                <div className="collapse-content gap-2  ">
                                    <ColorPicker  typee='base_layout' className="col-span-2"/>




                                </div>
                            </div>



                        </>

                    }
                    {
                        editorTypeRight.type === 'editor_color' &&
                        <div>
                            <ColorPicker typee='object'/>
                            {/*colores*/}
                        </div>

                    }
                    {
                        editorTypeRight.type === 'editor_params_3d' &&
                        <div>
                            {product?.paramSchema
                                ? <ParamPanel
                                    product={product}
                                    values={dataModelo3d}
                                    onChange={(path, value) =>
                                        setDataModelo3d(patchPath(dataModelo3d, path, value))
                                    }
                                    groupActions={{
                                        // Placing/removing the marker lives in the
                                        // hanging-slot panel, alongside the toggle,
                                        // so there is only one control for it.
                                        ...(product.roles?.keyhole ? {
                                            colgado: (
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-ghost w-full"
                                                    onClick={() => setEditorTypeRight({ type: 'editor_hole', data: null })}
                                                >
                                                    Colocar las ranuras en el lienzo →
                                                </button>
                                            ),
                                        } : {}),
                                        // Lo mismo para el perfil de base del servilletero.
                                        ...(product.roles?.napkinJoint ? {
                                            union: (
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-ghost w-full"
                                                    onClick={() => setEditorTypeRight({ type: 'editor_hole', data: null })}
                                                >
                                                    Colocar el perfil de base en el lienzo →
                                                </button>
                                            ),
                                        } : {}),
                                    }}
                                    footer={<ManifoldDiagnostics manifoldPoc={manifoldPoc}/>}
                                />
                                /* The keyring keeps its hand-written panel until
                                   it is ported to a schema of its own. */
                                : <ThiknessAndMoreEditor manifoldPoc={manifoldPoc}/>}
                        </div>

                    }
                    {
                        editorTypeRight.type === 'editor_shape' &&
                        <ShapePanel />
                    }
                    {
                        editorTypeRight.type === 'editor_offset_drawing' &&
                        <OffsetDrawingPanel
                            offsetLength={offsetLength}
                            onOffsetLengthChange={handleOffsetLength}
                            onApply={onChangeBaseLayout}
                            loadingAction={isBaseLayoutLoading ? activeBaseLayoutAction : null}
                            error={baseLayoutError}
                        />
                    }
                    {
                        editorTypeRight.type === 'editor_text' &&
                        <div>
                            <TextEditor/>
                        </div>
                    }
                    {
                        editorTypeRight.type === 'editor_history' &&
                        <HistoryPanel />
                    }
                    {
                        editorTypeRight.type === 'editor_layers' &&
                        <LayerPanel />
                    }
                    {
                        editorTypeRight.type === 'editor_pieces_3d' &&
                        <PartVisibilityPanel
                            parts={parts3d.parts}
                            visibility={parts3d.visibility}
                            onToggle={parts3d.onToggle}
                            onShowAll={parts3d.onShowAll}
                            onSolo={parts3d.onSolo}
                            colors={parts3d.colors}
                            onColorChange={parts3d.onColorChange}
                            onGroupColorChange={parts3d.onGroupColorChange}
                            onSaveColors={parts3d.onSaveColors}
                            hasColorChanges={parts3d.hasColorChanges}
                            isSavingColors={parts3d.isSavingColors}
                            canExplode={parts3d.canExplode}
                            partsLayout={parts3d.partsLayout}
                            onPartsLayoutChange={parts3d.onPartsLayoutChange}
                            separationMm={parts3d.separationMm}
                            onSeparationChange={parts3d.onSeparationChange}
                            sectionEnabled={parts3d.sectionEnabled}
                            onSectionEnabledChange={parts3d.onSectionEnabledChange}
                            sectionHeightPct={parts3d.sectionHeightPct}
                            onSectionHeightChange={parts3d.onSectionHeightChange}
                            sectionKeep={parts3d.sectionKeep}
                            onSectionKeepChange={parts3d.onSectionKeepChange}
                        />
                    }
                </div>
            </div>
            <div className="w-full pr-3 pt-5 bottom-0 max-h-36">

            </div>
        </div>
)
}


export default ToolSelectorRight
